import React from 'react';
import { Box, Text } from 'ink';
import type { WelcomePrompt, WelcomePromptCategory } from '../../types';
import { useFocusIndex } from '../hooks/use_focus_index';
import { useInputAction, usePublishInputContext } from '../keymap';
import {
  DEFAULT_WELCOME_CATEGORIES,
  welcomePromptDisplayText,
  welcomePromptRunText,
} from '../welcome_prompts';
import { useTheme } from '../theme';
import { Spinner } from './Spinner';

interface CategoryChipProps {
  label: string;
  selected: boolean;
  active: boolean;
}

const CategoryChip: React.FC<CategoryChipProps> = ({ label, selected, active }) => {
  const { tokens } = useTheme();
  return selected ? (
    <Text
      bold={active}
      backgroundColor={active ? tokens.text.success : tokens.text.subtle}
      color={tokens.text.inverse}
    >
      {` ${label} `}
    </Text>
  ) : (
    <Text color={tokens.text.subtle}>{` ${label} `}</Text>
  );
};

interface ExampleRowProps {
  example: WelcomePrompt;
  focused: boolean;
}

/**
 * Renders one example. Contextual prompts are segmented: the work item
 * reference (`!5` / `#7`) and its title render bold in a distinct color so
 * the work item stands out from the action text without needing delimiters
 * (titles routinely contain brackets themselves). Generic prompts are plain
 * strings.
 */
const ExampleRow: React.FC<ExampleRowProps> = ({ example, focused }) => {
  const { tokens } = useTheme();
  const baseColor = focused ? tokens.text.success : undefined;

  return (
    <Box paddingX={1} gap={1}>
      <Box flexShrink={0}>
        <Text bold color={tokens.text.success}>
          {focused ? '→' : ' '}
        </Text>
      </Box>
      {typeof example === 'string' ? (
        <Text color={baseColor}>{example}</Text>
      ) : (
        <Text color={baseColor}>
          {example.map((segment, index) =>
            segment.kind === 'text' ? (
              <Text key={`${segment.kind}-${index}`}>{segment.text}</Text>
            ) : (
              <Text
                key={`${segment.kind}-${index}`}
                bold
                color={focused ? tokens.text.success : tokens.text.accent}
              >
                {segment.text}
              </Text>
            ),
          )}
        </Text>
      )}
    </Box>
  );
};

export interface WelcomeCardProps {
  /** Runs the focused example as a prompt — typically the chat submit handler. */
  onRun: (value: string) => void | Promise<void>;
  /**
   * Whether the row selection is active. False once the user starts typing a
   * prompt: the focus marker/highlight are cleared so it's clear Enter submits
   * the message, not an example. The selected category and row are preserved,
   * so clearing the input restores the highlight. Defaults to true.
   */
  active?: boolean;
  /**
   * Prompt categories to display. `undefined` means the final lists are still
   * being resolved (contextual prompts are fetched at startup) — the card
   * shows the tabs with a loader until the caller provides the lists, and
   * never swaps them afterwards.
   */
  categories?: WelcomePromptCategory[];
}

/**
 * Shown on the empty chat screen to help a returning user kick off common work.
 */
export const WelcomeCard: React.FC<WelcomeCardProps> = ({ onRun, active = true, categories }) => {
  const { tokens } = useTheme();
  const displayedCategories: { label: string; examples?: WelcomePrompt[] }[] =
    categories ?? DEFAULT_WELCOME_CATEGORIES.map(({ label }) => ({ label }));

  const categoryFocus = useFocusIndex(displayedCategories.length);
  const currentCategory = displayedCategories[categoryFocus.index];
  const currentExamples = currentCategory?.examples;
  const rowFocus = useFocusIndex(currentExamples?.length ?? 0);

  // Switching category restarts row selection at the top so focus never lands
  // on a row that doesn't exist in the newly selected (possibly shorter) list.
  const changeCategory = (direction: 1 | -1) => {
    categoryFocus.move(direction);
    rowFocus.set(0);
  };

  // While loading there are no rows to pick, so the card must not claim the
  // arrow keys or Enter.
  usePublishInputContext({ welcomeActive: categories !== undefined });
  useInputAction('welcome.prevCategory', () => changeCategory(-1));
  useInputAction('welcome.nextCategory', () => changeCategory(1));
  useInputAction('welcome.prev', () => rowFocus.move(-1));
  useInputAction('welcome.next', () => rowFocus.move(1));
  useInputAction('welcome.run', () => {
    const example = currentExamples?.[rowFocus.index];
    return example ? onRun(welcomePromptRunText(example)) : undefined;
  });

  return (
    <Box
      borderStyle="round"
      borderColor={active ? tokens.text.success : tokens.text.subtle}
      paddingX={2}
      paddingY={1}
      marginBottom={1}
      flexDirection="column"
    >
      <Text>Get started with your work items:</Text>
      <Box marginTop={1} gap={1}>
        {displayedCategories.map((category, index) => (
          <CategoryChip
            key={category.label}
            label={category.label}
            selected={index === categoryFocus.index}
            active={active}
          />
        ))}
      </Box>
      <Box flexDirection="column" marginTop={1} gap={1}>
        {currentExamples ? (
          currentExamples.map((example, index) => (
            <ExampleRow
              key={welcomePromptDisplayText(example)}
              example={example}
              focused={active && index === rowFocus.index}
            />
          ))
        ) : (
          <Box paddingX={1}>
            <Spinner text="Finding relevant work items…" />
          </Box>
        )}
      </Box>
      <Box marginTop={1}>
        <Text dimColor>
          ← → to change categories · ↑↓ to choose · Enter to select · Type anything to start your
          question · /settings to toggle
        </Text>
      </Box>
    </Box>
  );
};
